import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Modal, Button, Badge, InputGroup, FormControl } from "react-bootstrap";
import { DataGrid, Column, Selection, Scrolling } from "devextreme-react/data-grid";
//pradip code start
// import { AppDispatch } from "slices/app/store/store";
//pradip code end
import Loader from "Common/Loader";
import { SetCunsumerId } from "slices/cunsumerOffer/reducer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "slices/app/store/store";
import { useSalesEntry } from "contexts/SalesEntryContext";

interface DiscountModalProps {
  show: boolean;
  onHide: () => void;
  discountAndGoldRateDetailsForCustomOrderData: Array<{
    discountOnMRPOptionId: number;
    discountTypeId: number;
    discountValue: number;
    isPercentage: boolean;
    fromDate: string;
    toDate: string;
    isStyleNo: boolean;
    styleNumber: string;
    isDefault: boolean;
    invoiceRangeFrom: number;
    invoiceRangeTo: number;
    isActive: boolean;
    materialTypeId: number;
    materialName: string;
    discountOnMRPName: string;
    discountTypeNmae: string;
    id: number; // Added id field
    isSelected?: boolean;
    isApplied?: boolean; // Track if offer was already applied
  }>;
  onDiscountApply?: (discount: any) => void; // Changed from toggle to apply
  appliedDiscountId?: number | null; // Track which discount is currently applied
  allowSelection?: boolean;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-IN", {
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
};

export const DiscountModal1: React.FC<DiscountModalProps> = ({
  show,
  onHide,
  discountAndGoldRateDetailsForCustomOrderData = [],
  onDiscountApply,
  appliedDiscountId = null,
  allowSelection = false
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<'all' | 'active' | 'percentage' | 'fixed'>('all');
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const dispatch = useDispatch()
  // Single selection state - only one offer can be selected at a time
  const [selectedDiscountId, setSelectedDiscountId] = useState<number | null>(appliedDiscountId);
  useEffect(() => {
    if (selectedDiscountId !== null) {
      dispatch(SetCunsumerId({ id: selectedDiscountId }));
    }
  }, [selectedDiscountId]);

  const cunsumerOfferId = useSelector((state: RootState) => state.CunsumerOfferId.cunsumerOfferId)

  // Add refs to prevent unnecessary re-renders
  const dataGridRef = useRef<any>(null);
  const modalStateRef = useRef({ isOpen: false });

  // Update modal state ref when show changes
  useEffect(() => {
    modalStateRef.current.isOpen = show;
  }, [show]);

  // Sync selected discount with applied discount when modal opens
  useEffect(() => {
    if (show) {
      if (appliedDiscountId !== null) {
        setSelectedDiscountId(appliedDiscountId);
      } else if (cunsumerOfferId !== null) {
        setSelectedDiscountId(cunsumerOfferId.id);
      } else {
        setSelectedDiscountId(null); // optional: clear selection if both are null
      }
    }
  }, [show, appliedDiscountId, cunsumerOfferId]);

  const reloadDataSource = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Memoize discount details with stable reference
  const discountDetails = useMemo(() => {
    const allDiscounts = discountAndGoldRateDetailsForCustomOrderData || [];
    const realDiscounts = allDiscounts.filter(discount =>
      typeof discount.id === 'number' && discount.id > 0
    );
    // console.log(realDiscounts)


    // Get currently selected discount
    const selectedDiscount = realDiscounts.find(
      d => d.id === (selectedDiscountId ?? cunsumerOfferId?.id)
    ) || null;


    return {
      allDiscounts: realDiscounts,
      selectedDiscount,
      hasDiscounts: realDiscounts.length > 0
    };
  }, [discountAndGoldRateDetailsForCustomOrderData, selectedDiscountId, cunsumerOfferId]);

  // Memoized filtered data
  const filteredDiscounts = useMemo(() => {
    let filtered = discountDetails.allDiscounts;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(discount =>
        discount.discountOnMRPName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discount.discountTypeNmae?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discount.materialName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discount.styleNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    switch (filterType) {
      case 'active':
        filtered = filtered.filter(d => d.isActive);
        break;
      case 'percentage':
        filtered = filtered.filter(d => d.isPercentage);
        break;
      case 'fixed':
        filtered = filtered.filter(d => !d.isPercentage);
        break;
      default:
        break;
    }

    return filtered;
  }, [discountDetails.allDiscounts, searchTerm, filterType]);

  // Statistics
  const discountStats = useMemo(() => {
    const total = discountDetails.allDiscounts.length;
    const active = discountDetails.allDiscounts.filter(d => d.isActive).length;
    const defaultCount = discountDetails.allDiscounts.filter(d => d.isDefault).length;
    const percentage = discountDetails.allDiscounts.filter(d => d.isPercentage).length;
    const fixed = total - percentage;
    const hasSelection = Boolean(selectedDiscountId ?? cunsumerOfferId?.id);


    // Calculate discount value from selected discount
    const selectedDiscount = discountDetails.selectedDiscount;
    const totalDiscount = selectedDiscount ? selectedDiscount.discountValue : 0;
    const totalPercentageDiscount = selectedDiscount?.isPercentage ? totalDiscount : 0;
    const totalFixedDiscount = selectedDiscount?.isPercentage ? 0 : totalDiscount;

    return {
      total,
      active,
      defaultCount,
      percentage,
      fixed,
      hasSelection,
      totalDiscount,
      totalPercentageDiscount,
      totalFixedDiscount,
      selectedDiscountName: selectedDiscount?.discountOnMRPName || ''
    };
  }, [discountDetails.allDiscounts, discountDetails.selectedDiscount, selectedDiscountId]);

  // Handle single selection change
  const handleDiscountSelect = useCallback((discountId: number) => {
    if (selectedDiscountId === discountId) {
      // Deselect if already selected
      setSelectedDiscountId(null);
    } else {
      // Select new discount (only one at a time)
      setSelectedDiscountId(discountId);
    }
  }, [selectedDiscountId]);

  // Handle clearing selection
  const handleClearSelection = useCallback(() => {
    setSelectedDiscountId(null);
    dispatch(SetCunsumerId(null));
  }, []);

  // Handle applying selected discount
  const handleApplyDiscount = useCallback(() => {
    if (selectedDiscountId && onDiscountApply) {
      const selectedDiscount = discountDetails.allDiscounts.find(
        d => d.id === selectedDiscountId
      );
      if (selectedDiscount) {
        onDiscountApply(selectedDiscount);
        onHide(); // Close modal after applying
      }
    }
  }, [selectedDiscountId, onDiscountApply, discountDetails.allDiscounts, onHide]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleFilterChange = useCallback((type: 'all' | 'active' | 'percentage' | 'fixed') => {
    setFilterType(type);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setFilterType('all');
  }, []);

  // Check if discount is currently selected
  const isDiscountSelected = useCallback((discountId: number) => {
    return (
      selectedDiscountId === discountId ||
      (cunsumerOfferId !== null && cunsumerOfferId.id === discountId)
    );
  }, [selectedDiscountId, cunsumerOfferId]);


  // Check if discount is currently applied
  const isDiscountApplied = useCallback((discountId: number) => {
    return appliedDiscountId === discountId;
  }, [appliedDiscountId]);

  // OPTIMIZED: Memoized column components
  const SelectColumn = useMemo(() => (
    <Column
      caption="Select"
      width={80}
      alignment="center"
      allowSorting={false}
      cellRender={({ data }) => (
        <div className="text-center">
          <input
            type="radio" // Changed to radio for single selection
            name="discountSelection"
            checked={isDiscountSelected(data.id)}
            onChange={() => handleDiscountSelect(data.id)}
            disabled={!data.isActive || isDiscountApplied(data.id)}
            className="form-check-input"
            style={{
              transform: 'scale(1.3)',
              cursor: data.isActive && !isDiscountApplied(data.id) ? 'pointer' : 'not-allowed'
            }}
          />
        </div>
      )}
    />
  ), [isDiscountSelected, isDiscountApplied, handleDiscountSelect]);

  const PromotionNameColumn = useMemo(() => (
    <Column
      caption="Promotion Name"
      width={200}
      cellRender={({ data }) => (
        <div>
          <div className={`fw-bold ${isDiscountSelected(data.id) ? 'text-primary' :
            isDiscountApplied(data.id) ? 'text-success' : 'text-dark'
            }`}>
            {data.promotionName || 'Unnamed Promotion'}
          </div>
          <div className="mt-1">
            {data.isActive ? (
              <Badge bg="success" className="me-2">Active</Badge>
            ) : (
              <Badge bg="secondary" className="me-2">Inactive</Badge>
            )}
            {data.isDefault && (
              <Badge bg="warning" className="text-dark me-2">Default</Badge>
            )}
            {isDiscountApplied(data.id) && (
              <Badge bg="info" className="me-2">Applied</Badge>
            )}
          </div>
        </div>
      )}
    />
  ), [isDiscountSelected, isDiscountApplied]);

  const DiscountTypeColumn = useMemo(() => (
    <Column
      caption="Discount Type"
      width={150}
      cellRender={({ data }) => (
        <div>
          <div className="fw-bold text-primary">
            {data.discountTypeNmae || 'Invoice Range'}
          </div>
          <div className="text-muted small">
            {data.isPercentage ? 'Percentage' : 'Fixed Amount'}
          </div>
        </div>
      )}
    />
  ), []);

  const ConsumerOfferOn = useMemo(() => (
    <Column
      caption="ConsumerOfferOn"
      width={180}
      cellRender={({ data }) => (
        <div className="small">
          {data.consumerOfferOn && <div>{data.consumerOfferOn}</div>}
        </div>
      )}
    />
  ), []);

  const DiscountValueColumn = useMemo(() => (
    <Column
      caption="Discount Value"
      width={130}
      alignment="center"
      cellRender={({ data }) => (
        <div className="text-center">
          {data.isPercentage ? (
            <Badge bg="info" className="fs-6">
              {data.discountValue || 0}%
            </Badge>
          ) : (
            <Badge bg="dark" className="fs-6">
              ₹{formatCurrency(data.discountValue || 0)}
            </Badge>
          )}
        </div>
      )}
    />
  ), []);

  const ProductInfoColumn = useMemo(() => (
    <Column
      caption="Product Info"
      width={180}
      cellRender={({ data }) => (
        <div className="small">
          {data.isStyleNo && data.styleNumber && (
            <div className="mb-1">
              <strong>Style:</strong> {data.styleNumber}
            </div>
          )}
          {data.materialName && (
            <div className="mb-1">
              <strong>Material:</strong> {data.materialName}
            </div>
          )}
          <div className="text-muted">
            <strong>Bag:</strong> {data.bagNumber || 'N/A'}
          </div>
        </div>
      )}
    />
  ), []);

  const InvoiceRangeColumn = useMemo(() => (
    <Column
      caption="Invoice Range"
      width={130}
      cellRender={({ data }) => (
        <div className="small">
          {data.invoiceRangeFrom > 0 || data.invoiceRangeTo > 0 ? (
            <div>
              <div>₹{formatCurrency(data.invoiceRangeFrom)}</div>
              <div>to ₹{formatCurrency(data.invoiceRangeTo)}</div>
            </div>
          ) : (
            <span className="text-muted">No Range</span>
          )}
        </div>
      )}
    />
  ), []);

  const ValidityColumn = useMemo(() => (
    <Column
      caption="Validity Period"
      width={130}
      cellRender={({ data }) => (
        <div className="small">
          {data.fromDate && data.toDate ? (
            <div>
              <div className="text-success">
                <strong>From:</strong> {new Date(data.fromDate).toLocaleDateString('en-GB')}
              </div>
              <div className="text-danger">
                <strong>To:</strong> {new Date(data.toDate).toLocaleDateString('en-GB')}
              </div>
            </div>
          ) : (
            <span className="text-muted">No validity period</span>
          )}
        </div>
      )}
    />
  ), []);

  const StatusColumn = useMemo(() => (
    <Column
      caption="Status"
      width={100}
      alignment="center"
      cellRender={({ data }) => (
        <div className="text-center">
          <Badge bg={data.isActive ? "success" : "secondary"}>
            {data.isActive ? (
              <>
                <i className="fa fa-check me-1"></i>Active
              </>
            ) : (
              <>
                <i className="fa fa-times me-1"></i>Inactive
              </>
            )}
          </Badge>
        </div>
      )}
    />
  ), []);

  // Filter buttons
  const FilterButtons = useMemo(() => (
    <div className="d-flex gap-2 mt-2">
      <Button
        variant={filterType === 'all' ? 'primary' : 'outline-primary'}
        size="sm"
        onClick={() => handleFilterChange('all')}
      >
        All ({discountStats.total})
      </Button>
      <Button
        variant={filterType === 'active' ? 'success' : 'outline-success'}
        size="sm"
        onClick={() => handleFilterChange('active')}
      >
        Active ({discountStats.active})
      </Button>
      <Button
        variant={filterType === 'percentage' ? 'info' : 'outline-info'}
        size="sm"
        onClick={() => handleFilterChange('percentage')}
      >
        Percentage ({discountStats.percentage})
      </Button>
      <Button
        variant={filterType === 'fixed' ? 'dark' : 'outline-dark'}
        size="sm"
        onClick={() => handleFilterChange('fixed')}
      >
        Fixed ({discountStats.fixed})
      </Button>
    </div>
  ), [filterType, handleFilterChange, discountStats]);

  // OPTIMIZED: Memoized modal component with stable structure
  const DiscountModalContent = useMemo(() => (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-light">
        <div className="d-flex justify-content-between align-items-center w-100">
          <Modal.Title>
            <span className="text-primary">
              <i className="fa fa-tag me-2"></i>
              Consumer Offer Details
            </span>
          </Modal.Title>
          {(cunsumerOfferId?.id || selectedDiscountId) && (
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearSelection}
                disabled={isBulkLoading}
              >
                <i className="fa fa-times me-1"></i>
                Clear Selection
              </Button>
            </div>
          )}
        </div>
      </Modal.Header>
      <p className="mb-0 pb-0"><strong>{' '}Please Note:</strong> <span className="text-danger">The selected offer will be verified at the time of final billing. Please be aware that the offer may not apply if the invoice amount falls outside the eligible discount range.</span></p>
      <Modal.Body className="p-0">
        {discountDetails.hasDiscounts ? (
          <>
            {/* Statistics Section */}
            <div className="p-3 bg-light border-bottom">
              <div className="row">
                <div className="col-md-3 text-center">
                  <h6 className="text-muted mb-1">Total Offers</h6>
                  <Badge bg="info" className="fs-6">{discountStats.total}</Badge>
                </div>
                <div className="col-md-3 text-center">
                  <h6 className="text-muted mb-1">Selected Offer</h6>
                  <Badge bg={discountStats.hasSelection ? "success" : "secondary"} className="fs-6">
                    {discountStats.hasSelection ? "1" : "0"}
                  </Badge>
                </div>
                <div className="col-md-3 text-center">
                  <h6 className="text-muted mb-1">Discount Value</h6>
                  <Badge bg="danger" className="fs-6">
                    {discountStats.totalPercentageDiscount > 0
                      ? `${discountStats.totalPercentageDiscount}%`
                      : `₹${formatCurrency(discountStats.totalFixedDiscount)}`}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="p-3 border-bottom">
              <div className="row">
                <div className="col-md-6">
                  <InputGroup>
                    <InputGroup.Text>
                      <i className="fa fa-search"></i>
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search discounts..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </InputGroup>
                </div>
              </div>
            </div>

            {/* Data Grid Section */}
            <div style={{ height: 'auto', padding: '10px' }}>
              {filteredDiscounts.length > 0 ? (
                <DataGrid
                  ref={dataGridRef}
                  dataSource={filteredDiscounts}
                  showBorders={true}
                  showColumnLines={true}
                  showRowLines={true}
                  allowColumnReordering={false}
                  allowColumnResizing={true}
                  columnAutoWidth={true}
                  rowAlternationEnabled={true}
                  keyExpr="id"
                  key={refreshKey}
                  noDataText="No discounts found matching your criteria"
                  repaintChangesOnly={true}
                  cacheEnabled={true}
                  hoverStateEnabled={true}
                  onContentReady={(e) => {
                    if (e.component) {
                      e.component.option('loadPanel.enabled', false);
                    }
                  }}
                >
                  {SelectColumn}
                  {PromotionNameColumn}
                  {ConsumerOfferOn}
                  {DiscountTypeColumn}
                  {ProductInfoColumn}
                  {DiscountValueColumn}
                  {ValidityColumn}
                  {InvoiceRangeColumn}
                  {StatusColumn}
                  <Scrolling
                    mode="standard"
                    useNative={true}
                    scrollByContent={true}
                    scrollByThumb={true}
                  />
                </DataGrid>
              ) : (
                <div className="text-center py-5">
                  <i className="fa fa-filter fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Discounts Found</h5>
                  <p className="text-muted">
                    {searchTerm || filterType !== 'all'
                      ? 'Try adjusting your search or filter criteria'
                      : 'No discount data available'
                    }
                  </p>
                  {(searchTerm || filterType !== 'all') && (
                    <Button variant="outline-primary" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="fa fa-tag fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Discounts Available</h5>
            <p className="text-muted">
              {discountAndGoldRateDetailsForCustomOrderData.length === 0
                ? "No discount options are available for this order."
                : "No valid discount options found for the selected products."
              }
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted small">
            <i className="fa fa-info-circle me-1"></i>
            {discountDetails.hasDiscounts ? (
              selectedDiscountId
                ? `1 offer selected - Only one offer can be applied at a time`
                : 'Select one offer to apply discount to your purchase'
            ) : (
              'No offers available for current selection'
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Close
            </Button>
            {allowSelection && discountDetails.hasDiscounts && (
              <Button
                variant="primary"
                onClick={handleApplyDiscount}
                disabled={!selectedDiscountId}
              >
                Apply Selected Offer
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  ), [
    show,
    onHide,
    allowSelection,
    discountDetails,
    isBulkLoading,
    handleClearSelection,
    searchTerm,
    filterType,
    handleSearchChange,
    FilterButtons,
    clearFilters,
    filteredDiscounts,
    discountStats,
    selectedDiscountId,
    appliedDiscountId,
    SelectColumn,
    PromotionNameColumn,
    DiscountTypeColumn,
    ProductInfoColumn,
    DiscountValueColumn,
    ValidityColumn,
    InvoiceRangeColumn,
    StatusColumn,
    refreshKey,
    discountAndGoldRateDetailsForCustomOrderData.length,
    handleApplyDiscount
  ]);

  return (
    <>
      {isBulkLoading && <Loader />}
      {DiscountModalContent}
    </>
  );
};